import type { CBClient } from './client';

/**
 * Options pour la connexion Google OAuth.
 */
export interface SignInWithGoogleOptions {
  /**
   * URL de redirection après connexion.
   *
   * Par défaut : `window.location.origin` (si disponible).
   *
   * @example 'https://cbimmodoc.mybestplace.immo/auth/callback'
   */
  redirectTo?: string;

  /**
   * Paramètres de requête additionnels pour Google OAuth.
   *
   * Par défaut : `{ access_type: 'offline', prompt: 'consent' }`
   * pour forcer le refresh token.
   */
  queryParams?: Record<string, string>;
}

/**
 * Lance le flow de connexion Google OAuth.
 *
 * L'utilisateur sera redirigé vers la page de connexion Google,
 * puis redirigé vers `redirectTo` après authentification.
 *
 * @param client - Client cb-core
 * @param options - Options de connexion
 * @returns Résultat de la tentative de connexion
 *
 * @example
 * ```typescript
 * const { data, error } = await signInWithGoogle(client, {
 *   redirectTo: 'https://cbimmodoc.mybestplace.immo/dashboard',
 * });
 *
 * if (error) {
 *   console.error('Login failed:', error);
 * }
 * // L'utilisateur est redirigé vers Google...
 * ```
 */
export async function signInWithGoogle(
  client: CBClient,
  options: SignInWithGoogleOptions = {}
) {
  const redirectTo =
    options.redirectTo ??
    (typeof window !== 'undefined' ? window.location.origin : undefined);

  const queryParams = options.queryParams ?? {
    access_type: 'offline',
    prompt: 'consent',
  };

  return client.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      queryParams,
    },
  });
}

/**
 * Déconnecte l'utilisateur courant.
 *
 * Supprime la session locale et révoque les tokens côté serveur.
 *
 * @param client - Client cb-core
 * @returns Résultat de la déconnexion
 *
 * @example
 * ```typescript
 * const { error } = await signOut(client);
 * if (!error) {
 *   // Rediriger vers la page de login
 *   window.location.href = '/login';
 * }
 * ```
 */
export async function signOut(client: CBClient) {
  return client.auth.signOut();
}

/**
 * Récupère la session courante de l'utilisateur.
 *
 * Retourne `null` si aucune session active.
 *
 * @param client - Client cb-core
 * @returns Session courante ou null
 *
 * @example
 * ```typescript
 * const { data: { session }, error } = await getSession(client);
 *
 * if (session) {
 *   console.log('User:', session.user.email);
 *   console.log('Access token:', session.access_token);
 * }
 * ```
 */
export async function getSession(client: CBClient) {
  return client.auth.getSession();
}

/**
 * Données de l'agent courant avec ses agences et rôles.
 */
export interface CurrentAgent {
  id: string;
  email: string;
  nom: string;
  prenom: string | null;
  telephone: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  /**
   * Agences de l'agent avec leur rôle.
   */
  agency_members: Array<{
    agency_id: string;
    role: 'agent' | 'assistant' | 'manager';
    is_primary: boolean;
  }>;

  /**
   * Rôles plateforme (admin_reseau, admin_technique).
   */
  platform_roles: Array<{
    role: 'admin_reseau' | 'admin_technique';
  }>;
}

/**
 * Récupère l'agent courant avec ses agences et rôles.
 *
 * Retourne `null` si l'utilisateur n'est pas connecté.
 *
 * @param client - Client cb-core
 * @returns Agent courant ou null
 *
 * @throws Si l'utilisateur est connecté mais l'agent n'existe pas en DB
 *         (ne devrait jamais arriver grâce au trigger `handle_new_user`)
 *
 * @example
 * ```typescript
 * const agent = await getCurrentAgent(client);
 *
 * if (!agent) {
 *   // Non connecté
 *   return <LoginPage />;
 * }
 *
 * console.log(`Bonjour ${agent.prenom} ${agent.nom}`);
 *
 * // Vérifier si admin
 * const isAdmin = agent.platform_roles.some(
 *   r => r.role === 'admin_technique'
 * );
 *
 * // Vérifier si manager d'une agence
 * const isManager = agent.agency_members.some(
 *   m => m.role === 'manager'
 * );
 *
 * // Récupérer l'agence primaire
 * const primaryAgency = agent.agency_members.find(m => m.is_primary);
 * ```
 */
export async function getCurrentAgent(
  client: CBClient
): Promise<CurrentAgent | null> {
  // Récupérer l'utilisateur auth
  const {
    data: { user },
  } = await client.auth.getUser();

  if (!user) {
    return null;
  }

  // Faire 3 requêtes complètement séparées pour éviter toute récursion RLS

  // 1. Récupérer l'agent
  const { data: agentData, error: agentError } = await client
    .from('agents')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  if (agentError) {
    throw new Error(`Failed to fetch current agent: ${agentError.message}`);
  }

  if (!agentData) {
    return null;
  }

  // 2. Récupérer les agency_members
  const { data: agencyMembers, error: membersError } = await client
    .from('agency_members')
    .select('agency_id, role, is_primary')
    .eq('agent_id', user.id);

  if (membersError) {
    throw new Error(`Failed to fetch agency members: ${membersError.message}`);
  }

  // 3. Récupérer les platform_roles via RPC
  const { data: platformRoles, error: rolesError } = await client.rpc(
    'get_agent_platform_roles',
    { p_agent_id: user.id }
  );

  if (rolesError) {
    throw new Error(`Failed to fetch platform roles: ${rolesError.message}`);
  }

  // Combiner les données
  return {
    ...agentData,
    agency_members: agencyMembers || [],
    platform_roles: platformRoles || [],
  } as CurrentAgent;
}
